import React, {PropsWithChildren, ReactNode, useMemo} from "react";
import {EmailHeaderView} from "./email/EmailHeaderView";
import "./ContentView.scss";
import {Centre} from "./Centre";
import ItemView from "./item/ItemView";
import classNames from "classnames";
import {FolderTypeEnum} from "../domain/Folder";
import {ContactListView} from "./contacts/ContactListView";
import {AccountsListView} from "./accounts/AccountsListView";
import {hasItemContent, isDummyContent} from '../domain/ItemContent';
import OutboxHeaderView from './email/OutboxHeaderView';
import {CalendarView} from './calendar/CalendarView';
import FolderSelectionOverlay from './folder/FolderSelectionOverlay';
import {SmallLoadingIndicator} from "./common/SmallLoadingIndicator";
import {ErrorBoundary} from "./common/ErrorBoundary";
import {FilesView} from "./files/FilesView";
import {useAppSelector} from "../hooks/ReduxHooks";
import DisconnectedIcon from '@mui/icons-material/PowerOff';
import WarningIcon from '@mui/icons-material/Warning';
import {pluralise} from "../util/DateHelper";
import {ApiVersion} from "../APIVersion";
import {getItemContent} from "../state/ItemState";
import {NotesView} from "./notes/NotesView";
import {LoadingIndicator} from "./common/LoadingIndicator";

interface ContentViewContainerProps {
    hasContent: boolean;
    contentExpanded?: boolean;
    itemsListElement?: ReactNode;
    contentElement?: ReactNode;
}

const SwappingLoadingIndicator = () => {
    const isLoadingContent = useAppSelector(s => {
        const itemContent = getItemContent(s.itemState);
        return isDummyContent(itemContent) && itemContent.dummyType === "loading";
    });

    return <div className={classNames("content-swapper", "content-swapper-loading-indicator", {"current-swapped-content": isLoadingContent})}>
        <Centre className="loading-overlay">
            <LoadingIndicator/>
        </Centre>
    </div>;
}

const SwappedContent = ({visible, content}: {visible: boolean, content: ReactNode}) => {
    return (
        <div className={classNames("content-swapper", {"current-swapped-content": visible})}>
            {content}
        </div>
    );
}

const SwappedContentContainer = ({children}: PropsWithChildren) => {
    return (
        <div className="content-swap-container">
            {children}

            <SwappingLoadingIndicator/>
        </div>
    );
}

export const ContentViewContainer = ({hasContent, contentExpanded, itemsListElement, contentElement}: ContentViewContainerProps) => {
    const isSmallScreen = useAppSelector(s => s.system.isSmallScreen);
    const itemContentIndex = useAppSelector(s => s.itemState.itemContentIndex);

    const content0 = useMemo(() => <ItemView itemContentIndex={0}/>, []);
    const content1 = useMemo(() => <ItemView itemContentIndex={1}/>, []);

    if (!itemsListElement) {
        return <div className="content-view">
            {contentElement}
        </div>;
    }

    let viewContent: ReactNode;
    if (isSmallScreen) {
        const currentItemContent = (itemContentIndex === (hasContent ? 0 : 1)) ? content0 : content1;
        viewContent = (
            <SwappedContentContainer>
                <SwappedContent visible={!hasContent} content={itemsListElement}/>
                <SwappedContent visible={hasContent} content={currentItemContent}/>
            </SwappedContentContainer>
        );
    } else if (itemsListElement && contentElement) {
        viewContent = <>
            {itemsListElement}
            {contentElement}
        </>;
    } else {
        viewContent = <>
            {itemsListElement}
            <SwappedContentContainer>
                <SwappedContent visible={itemContentIndex === 0} content={content0}/>
                <SwappedContent visible={itemContentIndex === 1} content={content1}/>
            </SwappedContentContainer>
        </>;
    }

    return <div className={classNames("content-view", {
        "expanded-content": contentExpanded && hasContent
    })}>
        {viewContent}
    </div>;
}

export const ContentView = () => {
    let content: ReactNode;

    // const username = useAppSelector(s => s.system.userName);
    const isOffline = useAppSelector(s => s.system.isOffline);
    const isClientOutOfDate = useAppSelector(s => s.system.serverApiVersion > ApiVersion);
    const selectedFolderType = useAppSelector(s => s.folderState.selectedFolder?.type);
    const syncInProgress = useAppSelector(s => s.sync.syncInProgress);
    const showFolderSelectionOverlay = useAppSelector(s => !!s.folderState.folderSelectionMode);
    const hasContent = useAppSelector(s => hasItemContent(getItemContent(s.itemState)));
    const contentExpanded = useAppSelector(s => s.itemState.contentExpanded);
    const pendingSendEmailCount = useAppSelector(s => s.sync.operations.filter(o => o.SendEmail).length);

    if (!selectedFolderType) {
        content = (<Centre className="empty-content">Select a folder</Centre>);

    } else if (selectedFolderType === FolderTypeEnum.Contacts) {
        content = <ContentViewContainer hasContent={hasContent}
                                        contentExpanded={contentExpanded}
                                        itemsListElement={<ContactListView/>}
        />;

    } else if (selectedFolderType === FolderTypeEnum.Calendar) {
        content = <ContentViewContainer hasContent={hasContent}
                                        contentExpanded={contentExpanded}
                                        itemsListElement={<CalendarView/>}
        />;

    } else if (selectedFolderType === FolderTypeEnum.Notes) {
        content = <ContentViewContainer hasContent={hasContent}
                                        contentExpanded={contentExpanded}
                                        contentElement={<NotesView/>}
        />;

    } else if (selectedFolderType === FolderTypeEnum.Files || selectedFolderType === FolderTypeEnum.ProtectedFiles || selectedFolderType === FolderTypeEnum.Attachments) {
        // TODO: do we need content view here container here?
        content = <div className="content-view"><FilesView/></div>;

    } else {
        const listView = getFolderListView(selectedFolderType);

        content = <ContentViewContainer hasContent={hasContent}
                                        contentExpanded={contentExpanded}
                                        itemsListElement={listView}
        />;
    }

    const emailText = pluralise(pendingSendEmailCount, "email", true);
    return (
        <div className="content-pane" style={{minWidth: "300px"}}>
            <ErrorBoundary>
                {content}
            </ErrorBoundary>

            <Centre className={classNames("sync-offline", {"visible": isOffline})}>
                <DisconnectedIcon/>
                {/*{username && ' - you must reauthenticate'}*/}
                <div>Not connected{!!pendingSendEmailCount && ` - ${emailText} will be sent when online`}</div>

            </Centre>

            <Centre className={classNames("sync-in-progress", {"visible": syncInProgress})}>
                <SmallLoadingIndicator/>
                <div>Syncing in progress...</div>
            </Centre>

            <Centre className={classNames("sync-in-progress", {"visible": pendingSendEmailCount > 0 && !isOffline})}>
                <SmallLoadingIndicator/>
                <div>Uploading {emailText}...</div>
            </Centre>

            <Centre className={classNames("sync-in-progress", "warning", {"visible": isClientOutOfDate})}>
                <WarningIcon/>
                <div>NB: Some operations may not function until you reload the page</div>
            </Centre>

            {showFolderSelectionOverlay ? <FolderSelectionOverlay/> : null}
        </div>
    );
};

function getFolderListView(folderType: FolderTypeEnum): ReactNode {
    switch (folderType) {
        case FolderTypeEnum.Accounts:
            return <AccountsListView/>;
        case FolderTypeEnum.Outbox:
            return <OutboxHeaderView/>;
    }
    return <EmailHeaderView/>;
}
